import React, { useCallback, useMemo, useRef, useState } from 'react';
import { SCENES } from './scenes';
import {
  inspectChain, controlFor, type InspectedNode, type Mode, type ControlTab,
} from './inspect';
import { CHROME_FONT_SIZE } from './controls';

/**
 * The preview: a real screen's shape, in the mode being edited, with an
 * inspector that answers "which control changes this".
 *
 * Only one mode renders. The old side-by-side pair halved the width of both and
 * meant half the preview was always showing values the controls were not
 * editing; the mode toggle already says which set is live, and switching it
 * moves the whole document, so what you see here matches what you see around it.
 */

interface Props {
  mode: Mode;
  /** Jump the editor to the control that changes `token`. */
  onJumpTo: (tab: ControlTab, token: string) => void;
  /** Density the theme asks for; the editor's own chrome stays pinned to stock. */
  spaceUnit: string;
}

const STORAGE_KEY = 'customize_scene';

function initialScene(): string {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SCENES.some((s) => s.id === saved)) return saved;
  } catch {
    // Private windows and blocked site data both throw here; the default is fine.
  }
  return SCENES[0].id;
}

const ScenePreview: React.FC<Props> = ({ mode, onJumpTo, spaceUnit }) => {
  const [sceneId, setSceneId] = useState(initialScene);
  const [inspecting, setInspecting] = useState(true);
  const [chain, setChain] = useState<InspectedNode[]>([]);
  const [box, setBox] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  const scene = useMemo(() => SCENES.find((s) => s.id === sceneId) ?? SCENES[0], [sceneId]);

  const chooseScene = (id: string) => {
    setSceneId(id);
    setChain([]);
    setBox(null);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // Remembering the last scene is a convenience, not state worth reporting.
    }
  };

  const onMove = useCallback((e: React.MouseEvent) => {
    const stage = stageRef.current;
    if (!inspecting || !stage) return;
    const target = e.target as Element;
    if (!(target instanceof Element) || !stage.contains(target)) return;

    setChain(inspectChain(target, stage, mode));
    const t = target.getBoundingClientRect();
    const s = stage.getBoundingClientRect();
    setBox({ top: t.top - s.top, left: t.left - s.left, width: t.width, height: t.height });
  }, [inspecting, mode]);

  const clear = useCallback(() => {
    setChain([]);
    setBox(null);
  }, []);

  return (
    <div style={{ fontSize: CHROME_FONT_SIZE }}>
      {/* Scene picker */}
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {SCENES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => chooseScene(s.id)}
            title={s.hint}
            className={`rounded border px-2 py-1 text-xs ${s.id === scene.id
              ? 'border-blue-600 bg-blue-600 text-white'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 '
                + 'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'}`}
          >
            {s.label}
          </button>
        ))}

        <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            checked={inspecting}
            onChange={(e) => { setInspecting(e.target.checked); clear(); }}
          />
          <span>Inspect on hover</span>
        </label>
      </div>

      <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
        {scene.hint}
        {' · '}
        A faithful sample, not the live page — same markup and tokens, fixed data.
      </div>

      {/* Stage */}
      <div
        ref={stageRef}
        onMouseMove={onMove}
        onMouseLeave={clear}
        onClick={() => {
          const first = chain[0]?.hits.find((h) => h.active) ?? chain[0]?.hits[0];
          const loc = first && controlFor(first.token);
          if (first && loc?.tab) onJumpTo(loc.tab, first.token);
        }}
        className={`theme-preview relative overflow-hidden rounded border border-gray-200 dark:border-gray-700 ${
          inspecting ? 'cursor-crosshair' : ''
        }`}
        style={{ '--space-unit': spaceUnit } as React.CSSProperties}
      >
        {scene.render()}

        {inspecting && box && (
          <div
            className="pointer-events-none absolute z-10 rounded-[1px] outline outline-2 outline-blue-500"
            style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
          />
        )}
      </div>

      <Readout chain={chain} inspecting={inspecting} onJumpTo={onJumpTo} />
    </div>
  );
};

const Readout: React.FC<{
  chain: InspectedNode[];
  inspecting: boolean;
  onJumpTo: (tab: ControlTab, token: string) => void;
}> = ({ chain, inspecting, onJumpTo }) => {
  if (!inspecting) return null;

  if (!chain.length) {
    return (
      <div className="mt-2 rounded border border-dashed border-gray-300 px-3 py-2 text-xs
                      text-gray-500 dark:border-gray-600 dark:text-gray-400"
      >
        Point at anything above to see which theme control paints it.
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1.5 rounded border border-gray-200 bg-gray-50 p-2
                    dark:border-gray-700 dark:bg-gray-800/50"
    >
      {chain.map((node) => (
        <div key={`${node.depth}-${node.label}`}>
          <div className="text-[0.6875rem] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            {node.depth === 0 ? node.label : `↑ ${node.label}`}
          </div>
          <ul className="mt-0.5 space-y-0.5">
            {node.hits.map((hit) => {
              const loc = controlFor(hit.token);
              return (
                <li key={hit.className} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                  <span className={`w-24 shrink-0 ${hit.active
                    ? 'text-gray-700 dark:text-gray-200'
                    : 'text-gray-400 dark:text-gray-500'}`}
                  >
                    {hit.property}
                  </span>
                  {loc.tab ? (
                    <button
                      type="button"
                      onClick={() => onJumpTo(loc.tab as ControlTab, hit.token)}
                      className="font-medium text-blue-700 underline decoration-dotted
                                 underline-offset-2 hover:decoration-solid dark:text-blue-300"
                    >
                      {hit.token}
                    </button>
                  ) : (
                    <span className="font-medium text-gray-500 dark:text-gray-400">{hit.token}</span>
                  )}
                  <span className="text-gray-400 dark:text-gray-500">
                    {loc.tab ? loc.note : loc.note}
                  </span>
                  {!hit.active && (
                    <span className="text-gray-400 dark:text-gray-500">
                      — {hit.stateful ? `only on ${hit.variants.join(':')}` : 'overridden in this mode'}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default ScenePreview;
